import { useEffect, useState } from 'react';
import { data_from_server_post } from '@/shared/http/client';
import { specificAIModelLabel } from '@/features/model-setup/utils/specificAIModelLabel';
// ``POST /usecase-models`` returns the per-usecase catalog of fine-tuned
// ("SpecificAI") models that have a trained version (ready to deploy or
// already deployed). Response shape is:
//   { models: [{ id, display_name, version, ... }] }
// ``label`` is the version number (``V1.0``) shown in the Playground Add Model
// dropdown; ``value`` stays the distillation-event id used for inference.
// The endpoint requires both ``client_id`` and ``llm_usecase_id``; if either is
// missing we short-circuit to an empty catalog so callers can render an empty
// SpecificAI provider without making a request.
//
// ``isLoading`` is derived from whether this request key has a catalog yet,
// not from a ``useState`` flag. Playground starts with no usecase (hook
// disabled, loading false); when the task appears on the next render the
// fetch has not started, and a stale ``isLoading === false`` would let
// callers seed the teacher before V1 arrives.
export function useSpecificAIModels(clientId, llmUsecaseId, enabled = true) {
    const requestKey = enabled && clientId && llmUsecaseId
        ? `${clientId}:${llmUsecaseId}`
        : null;
    const [catalog, setCatalog] = useState({ key: null, models: [] });

    useEffect(() => {
        if (!requestKey) {
            setCatalog({ key: null, models: [] });
            return undefined;
        }

        let cancelled = false;

        data_from_server_post(
            'usecase-models',
            { client_id: clientId, llm_usecase_id: llmUsecaseId },
            (response) => {
                if (cancelled) return;
                const models = Array.isArray(response?.models) ? response.models : [];
                setCatalog({
                    key: requestKey,
                    models: models.map((entry) => ({
                        value: entry?.id ?? '',
                        label: specificAIModelLabel(entry),
                        version: entry?.version ?? null,
                        raw: entry,
                    })),
                });
            },
            (error) => {
                if (cancelled) return;
                console.error('Failed to fetch SpecificAI models:', error);
                setCatalog({ key: requestKey, models: [] });
            }
        );

        return () => {
            cancelled = true;
        };
    }, [requestKey, clientId, llmUsecaseId]);

    const isLoading = requestKey !== null && catalog.key !== requestKey;
    const specificAIModels = catalog.key === requestKey ? catalog.models : [];

    return {
        specificAIModels,
        isLoading,
    };
}
